import { useMemo } from "react";
import { useV2ListApprovalLevelsForAccountQuery } from "@generated";
import type {
  ApprovalLevelRow,
  ApprovalLevelKind,
  ApprovalTrigger,
  ApprovalLevelApprover,
} from "../types/ApprovalLevel";

export interface UseApprovalLevelsListResult {
  rows: ApprovalLevelRow[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export interface UseApprovalLevelsListArgs {
  archived?: boolean;
}

// Exported so Edit.tsx (and any future consumer of the single-record GET) can
// reuse the same JSON:API → client shape conversion.
export const extractApprovers = (raw: any): ApprovalLevelApprover[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((a: any) => {
    const userId = a["user-id"] ?? a.user_id;
    const lobIds = a["lob-ids"] ?? a.lob_ids;
    return {
      id: a.id != null ? String(a.id) : undefined,
      userId: userId != null ? String(userId) : "",
      name: a.name ?? "",
      required: !!a.required,
      lobIds: Array.isArray(lobIds) ? lobIds.map((n: any) => String(n)) : [],
    };
  });
};

export const useApprovalLevelsList = ({
  archived = false,
}: UseApprovalLevelsListArgs = {}): UseApprovalLevelsListResult => {
  const { data, isLoading, isFetching, refetch } =
    useV2ListApprovalLevelsForAccountQuery({
      filter: { active: !archived },
    });

  const rows: ApprovalLevelRow[] = useMemo(
    () =>
      data?.data?.map((record: any) => ({
        id: String(record.id),
        position: record.attributes?.position ?? 0,
        name: record.attributes?.name ?? "",
        kind: (record.attributes?.kind ?? "business") as ApprovalLevelKind,
        signaturesRequired: record.attributes?.["signatures-required"] ?? 1,
        submitterMayApprove: !!record.attributes?.["submitter-may-approve"],
        limitApprovalsToTeam: !!record.attributes?.["limit-approvals-to-team"],
        triggers: (record.attributes?.triggers ?? []) as ApprovalTrigger[],
        triggerDescription: record.attributes?.["trigger-description"] ?? "",
        approvers: extractApprovers(record.attributes?.approvers),
        deletedAt: record.attributes?.["deleted-at"] ?? null,
      })) ?? [],
    [data]
  );

  return {
    rows,
    totalCount: data?.meta?.["record-count"] ?? rows.length,
    isLoading,
    isFetching,
    refetch,
  };
};
